/**
 * HH Finance - Metric Card Component
 *
 * Displays a single financial metric using equa-patternlib Card
 */

import React from 'react';
import styled from 'styled-components';
import { Card } from '@shared/components/card';
import { formatCurrency } from '../utils';

export interface MetricCardProps {
  label: string;
  value: number | string;
  format?: 'currency' | 'number' | 'percent' | 'text';
  trend?: 'up' | 'down' | 'neutral';
  trendValue?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
}

const MetricContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.textSecondary || '#666'};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
`;

const Value = styled.div<{ trend?: 'up' | 'down' | 'neutral' }>`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme, trend }) =>
    trend === 'up' ? theme.success || '#22c55e' :
    trend === 'down' ? theme.error || '#ef4444' :
    theme.textPrimary || '#1a1a1a'};
`;

const TrendBadge = styled.span<{ trend: 'up' | 'down' | 'neutral' }>`
  font-size: 12px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  background-color: ${({ trend }) =>
    trend === 'up' ? '#dcfce7' :
    trend === 'down' ? '#fee2e2' :
    '#f3f4f6'};
  color: ${({ trend }) =>
    trend === 'up' ? '#16a34a' :
    trend === 'down' ? '#dc2626' :
    '#6b7280'};
`;

const IconWrapper = styled.div`
  font-size: 24px;
  margin-bottom: 4px;
`;

export const MetricCard: React.FC<MetricCardProps> = ({
  label,
  value,
  format = 'currency',
  trend,
  trendValue,
  icon,
  onClick,
}) => {
  const formattedValue = React.useMemo(() => {
    if (typeof value === 'string') return value;
    switch (format) {
      case 'currency':
        return formatCurrency(value);
      case 'percent':
        return `${value.toFixed(1)}%`;
      case 'number':
        return new Intl.NumberFormat('en-US').format(value);
      default:
        return String(value);
    }
  }, [value, format]);

  return (
    <Card onClick={onClick}>
      <MetricContainer>
        {icon && <IconWrapper>{icon}</IconWrapper>}
        <Label>{label}</Label>
        <ValueRow>
          <Value trend={trend}>{formattedValue}</Value>
          {trendValue && trend && (
            <TrendBadge trend={trend}>
              {trend === 'up' ? '↑' : trend === 'down' ? '↓' : ''} {trendValue}
            </TrendBadge>
          )}
        </ValueRow>
      </MetricContainer>
    </Card>
  );
};

export default MetricCard;
